import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    CircularProgress,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getConfigValue, saveConfig, setContentFilter } from '../../../redux/slices/config';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';
import { DEFAULT_PROFANITY_WORDS } from '../../../utils/contentFilter';

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const SectionCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const ActionsRow = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
});

const CountLine = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
}));

const WordsField = styled(TextField)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const normalizeWords = (raw) =>
    [...new Set(
        String(raw)
            .split(/[\n,]/)
            .map((w) => w.trim().toLowerCase())
            .filter((w) => w.length > 0)
    )];

const ContentFilter = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [enabled, setEnabled] = useState(true);
    const [profanityEnabled, setProfanityEnabled] = useState(true);
    const [wordsText, setWordsText] = useState('');

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = () => {
        setLoading(true);
        dispatch(getConfigValue({
            key: 'contentFilter',
            value: null,
            callback: (data) => {
                const val = data?.value;
                if (val) {
                    setEnabled(!!val.enabled);
                    setProfanityEnabled(!!val.profanity?.enabled);
                    setWordsText(Array.isArray(val.profanity?.words) ? val.profanity.words.join('\n') : '');
                } else {
                    // No config doc yet (fresh/unseeded env): seed the form with defaults so the
                    // admin's first Save creates the document via the normal saveConfig upsert.
                    setEnabled(true);
                    setProfanityEnabled(true);
                    setWordsText(DEFAULT_PROFANITY_WORDS.join('\n'));
                }
                setLoading(false);
            }
        }));
    };

    const { showPrompt, confirmLeave, cancelLeave, message: unsavedMessage } = useUnsavedChanges(isDirty);

    const wordCount = normalizeWords(wordsText).length;

    const handleSave = () => {
        const words = normalizeWords(wordsText);
        const value = {
            enabled,
            profanity: {
                enabled: profanityEnabled,
                words,
            },
        };
        setSaving(true);
        dispatch(saveConfig({
            key: 'contentFilter',
            value,
            callback: (data) => {
                setSaving(false);
                if (data?.status === 0) return;
                setIsDirty(false);
                dispatch(setContentFilter(value));
                dispatch(showAlert({ severity: 'success', message: 'Content filter saved successfully' }));
            }
        }));
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Content Filter"
                subtitle="Block comments that contain words on the list below. Posting and editing are rejected with a message asking the author to revise — nothing is censored automatically."
            />

            <ConfigSubMenu />

            {loading ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : (
                <>
                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            General
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enabled}
                                    onChange={(e) => { setIsDirty(true); setEnabled(e.target.checked); }}
                                />
                            }
                            label="Enable content filter"
                        />
                        <Typography variant="body2" color="text.secondary">
                            Master switch. When off, no comments are checked regardless of the settings below.
                        </Typography>
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Profanity
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={profanityEnabled}
                                    disabled={!enabled}
                                    onChange={(e) => { setIsDirty(true); setProfanityEnabled(e.target.checked); }}
                                />
                            }
                            label="Block profanity in comments"
                        />
                        <WordsField
                            fullWidth
                            multiline
                            minRows={8}
                            label="Blocked words"
                            value={wordsText}
                            disabled={!enabled || !profanityEnabled}
                            onChange={(e) => { setIsDirty(true); setWordsText(e.target.value); }}
                            helperText="One word or phrase per line (commas are also accepted). Matching is case-insensitive and blocks whole words only."
                        />
                        <CountLine variant="body2">
                            {wordCount} {wordCount === 1 ? 'entry' : 'entries'}
                        </CountLine>
                    </SectionCard>

                    <ActionsRow>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </ActionsRow>
                </>
            )}

            <ConfirmDialog
                open={showPrompt}
                onClose={cancelLeave}
                onConfirm={confirmLeave}
                title="Unsaved Changes"
                message={unsavedMessage}
                confirmLabel="Leave"
                confirmColor="warning"
            />
        </AdminPageContainer>
    );
};

export default ContentFilter;
